import React, { useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { formatDealCustomText } from '@/features/rapidstor/helpers';
import { useDealDisplay } from '@/journey-based-listing/hooks/useDealDisplay';
import { DealSelectorProps } from '@/journey-based-listing/components/UnitSelect/OptionSelector';

import classes from './DealToggleBar.module.scss';

export default function DealToggleBar({
    deals,
    noDealOption,
    onSelectDeal,
    onClearDeal,
    location,
}: DealSelectorProps) {
    const { t } = useTranslation();
    const { getDealPriceDisplay } = useDealDisplay();

    const totalOptions = deals.length + (noDealOption.show ? 1 : 0);
    const needsScroll = totalOptions > 3;

    const activeDealOption = useMemo(
        () => deals.find((d) => d.isActive) ?? null,
        [deals],
    );

    const barClassName = needsScroll
        ? classes.toggle_bar_scroll
        : classes.toggle_bar_container;

    return (
        <div>
            {needsScroll ? (
                <div className={classes.scroll_wrapper}>
                    <div className={classes.scroll_fade_left} />
                    <div className={barClassName}>
                        {noDealOption.show && (
                            <button
                                type="button"
                                className={`${classes.segment} ${classes.segment_scrollable} ${
                                    noDealOption.isActive ? classes.segment_active : ''
                                }`}
                                onClick={onClearDeal}
                            >
                                {t('labels:standard', 'Standard')}
                            </button>
                        )}
                        {deals.map((d) => (
                            <button
                                key={d.deal._id}
                                type="button"
                                className={`${classes.segment} ${classes.segment_scrollable} ${
                                    d.isActive ? classes.segment_active : ''
                                }`}
                                onClick={() => onSelectDeal(d.deal._id)}
                            >
                                {d.deal.title || t('labels:standard', 'Standard')}
                            </button>
                        ))}
                    </div>
                    <div className={classes.scroll_fade_right} />
                </div>
            ) : (
                <div className={barClassName}>
                    {noDealOption.show && (
                        <button
                            type="button"
                            className={`${classes.segment} ${
                                noDealOption.isActive ? classes.segment_active : ''
                            }`}
                            onClick={onClearDeal}
                        >
                            {t('labels:standard', 'Standard')}
                        </button>
                    )}
                    {deals.map((d) => (
                        <button
                            key={d.deal._id}
                            type="button"
                            className={`${classes.segment} ${
                                d.isActive ? classes.segment_active : ''
                            }`}
                            onClick={() => onSelectDeal(d.deal._id)}
                        >
                            {d.deal.title || t('labels:standard', 'Standard')}
                        </button>
                    ))}
                </div>
            )}

            <AnimatePresence>
                {activeDealOption && (
                    <motion.div
                        key="deal-detail"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                        style={{ overflow: 'hidden' }}
                    >
                        <div className={classes.detail_section}>
                            {activeDealOption.deal.customFormat?.primary && (
                                <div className={classes.detail_custom_text}>
                                    {formatDealCustomText(
                                        activeDealOption.deal,
                                        location,
                                        activeDealOption.deal.customFormat.primary,
                                    )}
                                </div>
                            )}
                            {activeDealOption.deal.customFormat?.secondary && (
                                <div className={classes.detail_custom_text}>
                                    {formatDealCustomText(
                                        activeDealOption.deal,
                                        location,
                                        activeDealOption.deal.customFormat.secondary,
                                    )}
                                </div>
                            )}

                            <div className={classes.detail_rate}>
                                {getDealPriceDisplay(activeDealOption.deal, activeDealOption.rate)}
                            </div>

                            {activeDealOption.deal.descriptions.length > 0 && (
                                <ul className={classes.detail_descriptions}>
                                    {activeDealOption.deal.descriptions.map((desc, idx) => (
                                        <li key={`${activeDealOption.deal._id}_desc_${idx}`}>
                                            {desc}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
